import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as d3 from 'd3';
import {
  FileCheck,
  Play,
  Loader2,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Code,
  Database,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnicodeCompliance = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  const gaugeRef = useRef(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (analysis && activeTab === 'overview') {
      renderReadinessGauge();
    }
  }, [analysis, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/unicode-compliance/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No Unicode compliance analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/unicode-compliance/analyze`, {});
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running Unicode compliance analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const renderReadinessGauge = () => {
    if (!gaugeRef.current || !analysis) return;

    const svg = d3.select(gaugeRef.current);
    svg.selectAll('*').remove();

    const width = 300;
    const height = 200;
    const radius = 90;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${width / 2}, ${height - 20})`);

    // Background arc
    const backgroundArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    g.append('path')
      .attr('d', backgroundArc)
      .attr('fill', '#E5E7EB');

    // Color zones
    const redArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(-Math.PI / 2 + (50 / 100) * Math.PI);

    const yellowArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (50 / 100) * Math.PI)
      .endAngle(-Math.PI / 2 + (80 / 100) * Math.PI);

    const greenArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (80 / 100) * Math.PI)
      .endAngle(Math.PI / 2);

    g.append('path').attr('d', redArc).attr('fill', '#EF4444').attr('opacity', 0.6);
    g.append('path').attr('d', yellowArc).attr('fill', '#F59E0B').attr('opacity', 0.6);
    g.append('path').attr('d', greenArc).attr('fill', '#10B981').attr('opacity', 0.6);

    // Current value
    const score = analysis.unicode_readiness_score.unicode_readiness_score;
    const valueAngle = -Math.PI / 2 + (score / 100) * Math.PI;

    const valueArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(valueAngle);

    const color = score >= 80 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
    g.append('path')
      .attr('d', valueArc)
      .attr('fill', color);

    // Center text
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', -30)
      .style('font-size', '36px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(score);

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 0)
      .style('font-size', '14px')
      .style('fill', '#6B7280')
      .text('Readiness Score');

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 20)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(analysis.unicode_readiness_score.readiness_level);
  };

  const exportBacklog = () => {
    if (!analysis) return;

    const csv = [
      ['Backlog ID', 'Task Type', 'Object', 'Description', 'Priority', 'Effort', 'Workstream'].join(','),
      ...analysis.remediation_plan.unicode_backlog.map(b => [
        b.backlog_id,
        b.task_type,
        b.object_name,
        `"${b.description}"`,
        b.priority,
        b.effort_band,
        b.workstream
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_Unicode_Remediation_${project.name.replace(/\s/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Unicode Compliance Analyzer</h2>
            <p className="text-gray-600">Evaluate Unicode readiness for S/4HANA conversion</p>
            
            {/* SAP System Indicator */}
            {analysis && analysis.sap_system_analyzed && analysis.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-teal-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-teal-600">{analysis.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({analysis.sap_system_analyzed.host})</span>
                </div>
                {analysis.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {analysis && (
              <button 
                onClick={exportBacklog}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export CSV</span>
              </button>
            )}
            <button 
              onClick={runAnalysis}
              disabled={analyzing}
              className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FileCheck className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Unicode Analysis</h3>
          <p className="text-gray-600 mb-6">Evaluate Unicode compliance for S/4HANA conversion</p>
          <button 
            onClick={runAnalysis}
            className="bg-green-600 text-white px-8 py-3 rounded-xl hover:bg-green-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Analysis</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className={`rounded-xl p-6 text-white shadow-lg ${
              analysis.baseline_state.is_unicode_system 
                ? 'bg-gradient-to-br from-green-500 to-green-600' 
                : 'bg-gradient-to-br from-red-500 to-red-600'
            }`}>
              {analysis.baseline_state.is_unicode_system ? (
                <CheckCircle2 className="w-10 h-10 mb-3 opacity-80" />
              ) : (
                <XCircle className="w-10 h-10 mb-3 opacity-80" />
              )}
              <div className="text-xl font-bold mb-2">
                {analysis.baseline_state.is_unicode_system ? 'Unicode' : 'Non-Unicode'}
              </div>
              <div className="text-sm opacity-80">System Status</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <TrendingUp className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.readiness_score}</div>
              <div className="text-blue-100 text-sm">Readiness Score</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.critical_count}</div>
              <div className="text-orange-100 text-sm">Critical Issues</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <FileCheck className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.backlog_items}</div>
              <div className="text-purple-100 text-sm">Remediation Items</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'abap', label: 'ABAP Issues', icon: Code },
                { id: 'ddic', label: 'DDIC Issues', icon: Database },
                { id: 'backlog', label: 'Remediation Plan', icon: FileCheck },
                { id: 'narrative', label: 'AI Summary', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Readiness Gauge */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 text-center">Unicode Readiness Score</h3>
                    <div className="flex justify-center">
                      <svg ref={gaugeRef}></svg>
                    </div>
                  </div>

                  {/* Baseline State */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">System Baseline</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Code Page:</p>
                        <p className="font-bold text-gray-900">{analysis.baseline_state.current_code_page}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Kernel Compatible:</p>
                        <p className={`font-bold ${analysis.baseline_state.kernel_unicode_compatible ? 'text-green-600' : 'text-red-600'}`}>
                          {analysis.baseline_state.kernel_unicode_compatible ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Complexity:</p>
                        <p className="font-bold text-gray-900 capitalize">{analysis.baseline_state.baseline_complexity}</p>
                      </div>
                    </div>
                  </div>

                  {/* Issue Summary */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">ABAP Code Issues</h4>
                      <div className="text-4xl font-bold text-red-600">{analysis.summary.total_abap_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Objects with compliance issues</p>
                    </div>
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">DDIC Issues</h4>
                      <div className="text-4xl font-bold text-orange-600">{analysis.summary.total_ddic_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Data dictionary issues</p>
                    </div>
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Interface Issues</h4>
                      <div className="text-4xl font-bold text-yellow-600">{analysis.summary.total_interface_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Integration points</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ABAP Issues Tab */}
              {activeTab === 'abap' && (
                <div className="space-y-4">
                  {analysis.abap_unicode_issues.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                      <p>No ABAP Unicode issues detected</p>
                    </div>
                  )}

                  {analysis.abap_unicode_issues.map((obj) => (
                    <div key={obj.object_name} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{obj.object_name}</h4>
                          <div className="flex flex-wrap gap-2 text-xs mb-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {obj.object_type}
                            </span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {obj.package}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold text-red-600">{obj.total_issues}</div>
                          <div className="text-xs text-gray-500">Total Issues</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {obj.issues.map((issue, idx) => (
                          <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                    issue.severity === 'CRITICAL' ? 'bg-red-600 text-white' :
                                    issue.severity === 'HIGH' ? 'bg-orange-500 text-white' :
                                    'bg-yellow-500 text-white'
                                  }`}>
                                    {issue.severity}
                                  </span>
                                  <p className="font-semibold text-gray-900">{issue.rule_name}</p>
                                </div>
                                <p className="text-sm text-gray-700">{issue.description}</p>
                                {issue.line_numbers.length > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Lines: {issue.line_numbers.join(', ')}
                                  </p>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 ml-4">
                                {issue.occurrences} occurrence(s)
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* DDIC Issues Tab */}
              {activeTab === 'ddic' && (
                <div className="space-y-4">
                  {analysis.ddic_unicode_issues.map((issue, idx) => (
                    <div key={idx} className="bg-white border-2 border-orange-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              issue.severity === 'CRITICAL' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
                            }`}>
                              {issue.severity}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{issue.object_name}</h4>
                          </div>
                          <p className="text-sm text-gray-600">Field: {issue.field_name} ({issue.field_type}, Length: {issue.field_length})</p>
                        </div>
                      </div>

                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Issue:</p>
                        <p className="text-sm text-gray-700">{issue.issue}</p>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Recommended Fix:</p>
                        <p className="text-sm text-gray-700">{issue.recommended_type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Remediation Backlog Tab */}
              {activeTab === 'backlog' && (
                <div className="space-y-6">
                  {/* Work Packages */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {analysis.remediation_plan.work_packages.map((pkg) => (
                      <div key={pkg.package_id} className="bg-white border-2 border-green-200 rounded-xl p-6">
                        <h4 className="font-bold text-gray-900 mb-4">{pkg.package_name}</h4>
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Total Tasks:</span>
                            <span className="font-bold">{pkg.total_tasks}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">P0 (Critical):</span>
                            <span className="font-bold text-red-600">{pkg.p0_count}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">P1 (High):</span>
                            <span className="font-bold text-orange-600">{pkg.p1_count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Backlog Items */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Remediation Backlog</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold">ID</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold">Task Type</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold">Object</th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-bold">Priority</th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-bold">Effort</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold">Workstream</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysis.remediation_plan.unicode_backlog.map((item) => (
                            <tr key={item.backlog_id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-2 font-mono text-xs">{item.backlog_id}</td>
                              <td className="border border-gray-300 px-4 py-2">{item.task_type}</td>
                              <td className="border border-gray-300 px-4 py-2 font-semibold">{item.object_name}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  item.priority === 'P0' ? 'bg-red-600 text-white' :
                                  item.priority === 'P1' ? 'bg-orange-500 text-white' :
                                  'bg-yellow-500 text-white'
                                }`}>
                                  {item.priority}
                                </span>
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-center font-bold">{item.effort_band}</td>
                              <td className="border border-gray-300 px-4 py-2">{item.workstream}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Narrative Tab */}
              {activeTab === 'narrative' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                      <Sparkles className="w-6 h-6 text-green-600" />
                      <span>Unicode Readiness Analysis</span>
                    </h3>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{analysis.ai_narrative}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Critical Action Items</h4>
                    <ul className="space-y-3">
                      {analysis.remediation_plan.unicode_backlog
                        .filter(item => item.priority === 'P0')
                        .slice(0, 5)
                        .map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                            <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{item.description}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs text-gray-600">{item.object_name}</span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-600">Effort: {item.effort_band}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UnicodeCompliance;
